import React from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  filterVehicles, getVehiclePath, getQuoteUrl, getServiceSelection,
  TERMS, families, getFamily, BODY_LABELS, POWERTRAIN_LABELS, getLengthLabel, getRoofLabel,
} from '../data/rentalCatalogue';
import './RentalCatalogue.css';

const unique = (values) => [...new Set(values)];

export default function RentalCatalogue({ mode = 'all', service: serviceProp, initialPowertrain }) {
  const location = useLocation();
  const route = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isElectricPage = mode === 'electric' || location.pathname === '/electric-hybrid-vans';
  const defaultService = serviceProp || (mode === 'hire' || location.pathname === '/van-hire' ? 'hire' : 'rental');
  const selection = getServiceSelection({ service: params.get('service') || defaultService, term: params.get('term') });
  const selectedPowertrain = params.get('powertrain') || initialPowertrain || (isElectricPage ? 'electrified' : 'all');
  const familyId = route.family || params.get('family') || '';
  const family = getFamily(familyId);
  const filters = {
    family: familyId,
    body: params.get('body') || '',
    powertrain: selectedPowertrain === 'all' ? '' : selectedPowertrain,
    length: params.get('length') || '',
    roof: params.get('roof') || '',
    trim: params.get('trim') || '',
  };
  const vehicles = filterVehicles(filters);
  const matchingFamily = filterVehicles({ family: filters.family });
  const bodies = unique(matchingFamily.map(vehicle => vehicle.body));
  const matchingBodyAndPowertrain = filterVehicles({ family: filters.family, body: filters.body, powertrain: filters.powertrain });
  const lengths = unique(matchingBodyAndPowertrain.flatMap(vehicle => vehicle.configurations.map(item => item.length)));
  const roofs = unique(matchingBodyAndPowertrain.flatMap(vehicle => vehicle.configurations.filter(item => !filters.length || item.length === filters.length).map(item => item.roof)));
  const trims = unique(matchingBodyAndPowertrain.flatMap((vehicle) => vehicle.configurations
    .filter((item) => (!filters.length || item.length === filters.length) && (!filters.roof || item.roof === filters.roof)).map((item) => item.trim)));

  function updateParam(key, value) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key === 'family') {
      ['body', 'length', 'roof', 'trim'].forEach(field => next.delete(field));
      if (route.family) {
        next.delete('family');
        navigate(`${value ? `/our-vans/ford/${value}` : '/our-vans'}?${next.toString()}`);
        return;
      }
    }
    if (['body', 'powertrain', 'length', 'roof'].includes(key)) next.delete('trim');
    if (['body', 'powertrain', 'length'].includes(key)) next.delete('roof');
    if (key === 'service') next.set('term', TERMS[value][0].value);
    setParams(next, { replace: true });
  }

  function resetFilters() {
    const next = new URLSearchParams(selection);
    if (isElectricPage) next.set('powertrain', 'electrified');
    setParams(next, { replace: true });
  }

  const isHire = selection.service === 'hire';
  const title = isElectricPage ? 'Electric & hybrid. Ready for your next move.'
    : mode === 'rental' || location.pathname === '/van-rental' ? 'A van for a day. A week. Or a little longer.'
      : mode === 'hire' || location.pathname === '/van-hire' ? 'Keep your business moving, month after month.'
        : family ? `Find your Ford ${family.label}.` : 'Find your next Ford van.';

  if (familyId && !family) return <main className="rental-section"><div className="rental-container rental-empty-state"><h1>We couldn’t find that Ford range.</h1><Link className="rental-button" to="/our-vans">Explore all vans</Link></div></main>;

  return (
    <main className="rental-catalogue-page">
      <section className="rental-catalogue-intro rental-section">
        <div className="rental-container">
          <p className="rental-eyebrow">Ford vans · Liverpool · Nationwide delivery</p>
          <h1>{title}</h1>
          <p className="rental-catalogue-intro__copy">
            {isElectricPage ? 'Explore fully electric and plug-in hybrid vans, with room for your tools, deliveries or team.'
              : 'From everyday deliveries to a whole crew on site. Explore the right body, powertrain and specification for the job.'}
          </p>
          <div className="rental-service-picker" aria-label="Choose your service">
            <button type="button" aria-pressed={!isHire} onClick={() => updateParam('service', 'rental')}>Rental <span>Daily, weekly or 28 days</span></button>
            <button type="button" aria-pressed={isHire} onClick={() => updateParam('service', 'hire')}>Hire <span>3, 6, 9 or 12 months</span></button>
          </div>
          <div className="rental-duration-row">
            <label htmlFor="catalogue-term">Your preferred term</label>
            <select id="catalogue-term" value={selection.term} onChange={(event) => updateParam('term', event.target.value)}>
              {TERMS[selection.service].map((term) => <option key={term.value} value={term.value}>{term.label}</option>)}
            </select>
            <p>{isHire ? 'Hire agreements are billed every 28 days. Your quote will explain the full term and final-period charge.'
              : 'Need more time? Ask our team about extending your rental, subject to agreement and availability.'}</p>
          </div>
        </div>
      </section>

      <section className="rental-section rental-catalogue-results" aria-labelledby="catalogue-heading">
        <div className="rental-container">
          <div className="rental-catalogue-heading">
            <div><p className="rental-eyebrow">{family ? `The ${family.label} range` : 'The Ford van range'}</p><h2 id="catalogue-heading">Choose the way you work.</h2></div>
            <p>Business first. Private customers welcome.</p>
          </div>
          <div className="rental-filter-bar" role="search" aria-label="Filter vans">
            <label>Model range<select value={familyId} onChange={event => updateParam('family', event.target.value)}><option value="">All Ford vans</option>{families.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
            <label>Body style<select value={filters.body} onChange={(event) => updateParam('body', event.target.value)}>
              <option value="">All body styles</option>{bodies.map(value => <option key={value} value={value}>{BODY_LABELS[value]}</option>)}
            </select></label>
            <label>Powertrain<select value={selectedPowertrain} onChange={(event) => updateParam('powertrain', event.target.value)}>
              <option value="all">All powertrains</option><option value="electrified">Electric & plug-in hybrid</option>{Object.entries(POWERTRAIN_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select></label>
            <label>Length<select value={filters.length} onChange={(event) => updateParam('length', event.target.value)}>
              <option value="">All lengths</option>{lengths.map(value => <option key={value} value={value}>{getLengthLabel(value, familyId ? matchingFamily[0] : undefined)}</option>)}
            </select></label>
            <label>Roof height<select value={filters.roof} onChange={event => updateParam('roof', event.target.value)}><option value="">All roof heights</option>{roofs.map(value => <option key={value} value={value}>{getRoofLabel(value)}</option>)}</select></label>
            <label>Trim<select value={filters.trim} onChange={(event) => updateParam('trim', event.target.value)}>
              <option value="">All trims</option>{trims.map((trim) => <option key={trim}>{trim}</option>)}
            </select></label>
            <button type="button" className="rental-filter-reset" onClick={resetFilters}>Reset filters</button>
          </div>
          <p className="rental-result-count" role="status">{vehicles.length} {vehicles.length === 1 ? 'vehicle type' : 'vehicle types'} · choose a vehicle to explore valid specifications</p>
          <div className="rental-vehicle-grid">
            {vehicles.map((vehicle) => {
              const configurations = vehicle.configurations.filter((item) => (!filters.length || item.length === filters.length) && (!filters.roof || item.roof === filters.roof) && (!filters.trim || item.trim === filters.trim));
              const cardSelection = { ...selection, length: filters.length, roof: filters.roof, trim: filters.trim };
              return (
                <article className="rental-vehicle-card" key={vehicle.id}>
                  <Link to={getVehiclePath(vehicle, cardSelection)} className="rental-vehicle-card__image-link" aria-label={`View ${vehicle.name}, ${vehicle.powertrainLabel}`}>
                    <img src={vehicle.image} alt={`Illustration of a ${vehicle.powertrainLabel.toLowerCase()} ${vehicle.familyLabel}`} width="900" height="600" loading="lazy" />
                    <span className={`rental-powertrain-badge rental-powertrain-badge--${vehicle.powertrain}`}>{vehicle.powertrainLabel}</span>
                  </Link>
                  <div className="rental-vehicle-card__content">
                    <p className="rental-vehicle-card__family">Ford {vehicle.familyLabel}</p>
                    <h3><Link to={getVehiclePath(vehicle, cardSelection)}>{vehicle.bodyLabel}</Link></h3>
                    <ul className="rental-vehicle-card__facts"><li>{vehicle.seatsLabel}</li><li>{unique(configurations.map(item => item.length)).join(' / ')} · {unique(configurations.map(item => getRoofLabel(item.roof))).join(' / ')}</li></ul>
                    <p className="rental-vehicle-card__trims">{unique(configurations.map((item) => item.trim)).join(' · ')}</p>
                    <div className="rental-vehicle-card__offer"><strong>Request a quote</strong><span>Availability to be confirmed</span></div>
                    <Link to={getVehiclePath(vehicle, cardSelection)} className="rental-button rental-vehicle-card__button">Explore this van <span aria-hidden="true">↗</span></Link>
                  </div>
                </article>
              );
            })}
          </div>
          {vehicles.length === 0 && <div className="rental-empty-state"><h3>Let’s find the right configuration.</h3><p>There are no matching vehicle types with these filters. Try another combination or tell our team what you need.</p><button type="button" className="rental-button rental-button--secondary" onClick={resetFilters}>Reset filters</button><Link className="rental-button" to={getQuoteUrl(null, selection)}>Ask our team</Link></div>}
          <p className="rental-catalogue-note">Manufacturer range information. Vanir availability, exact seating and specification will be confirmed by our team. Images are illustrative.</p>
        </div>
      </section>
      <section className="rental-section rental-catalogue-help"><div className="rental-container rental-catalogue-help__inner"><div><p className="rental-eyebrow">Have a different van in mind?</p><h2>Tell us what the job needs.</h2><p>Ask about other Ford vans, multiple vehicles or a particular specification.</p></div><Link className="rental-button" to={getQuoteUrl(null, selection)}>Request a quote <span aria-hidden="true">↗</span></Link></div></section>
    </main>
  );
}
